<template>
  <HrPageLayout
      title="My skills audit"
      subtitle=""
      icon="📝"
      :breadcrumb-items="[
        { label: 'Home', route: '/employee-dashboard' },
        { label: 'Employee Self-Service' },
        { label: 'My skills audit' }
      ]"
    >
      <div class="skills-self-wrap">
        <div class="card hr-mb-3">
          <div class="card-body">
            <div class="row g-3 align-items-end">
              <div class="col-md-3">
                <label class="form-label">Audit year</label>
                <select v-model.number="auditYear" class="form-select">
                  <option v-for="y in yearOptions" :key="'sy-' + y" :value="y">{{ y }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="card hr-mb-3 skills-form-card">
          <div class="card-body">
            <div class="skills-form-scroll">
              <section class="form-section" id="sec-skills">
                <h3 class="section-heading">Skills &amp; training</h3>
                <div class="row g-3">
                  <div class="col-12">
                    <label class="form-label">Description of specific skill <span class="text-danger">*</span></label>
                    <textarea v-model="form.skillDescription" class="form-control" rows="5" placeholder="Describe the skill or competency"></textarea>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Duration of the training / experience</label>
                    <input v-model="form.trainingDuration" type="text" class="form-control" placeholder="e.g. 2 years, 6 months">
                  </div>
                  <div class="col-12">
                    <label class="form-label">Evidence of skills</label>
                    <textarea v-model="form.evidenceSkills" class="form-control" rows="4" placeholder="Licences, deployments, reports, feedback…"></textarea>
                  </div>
                  <div class="col-12">
                    <label class="form-label">Training need</label>
                    <textarea v-model="form.trainingNeed" class="form-control" rows="4"></textarea>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Skills area</label>
                    <input v-model="form.skillsArea" type="text" class="form-control">
                  </div>
                  <div class="col-12">
                    <label class="form-label">How am I doing</label>
                    <textarea v-model="form.howAmIDoing" class="form-control" rows="3"></textarea>
                  </div>
                </div>
              </section>
            </div>

            <div class="skills-form-actions">
              <button type="button" class="btn btn-primary btn-lg" :disabled="submitting" @click="submit">
                {{ submitting ? "Submitting…" : "Submit skills audit" }}
              </button>
              <p v-if="msg" class="mt-3 mb-0" :class="ok ? 'text-success' : 'text-danger'">{{ msg }}</p>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <h6 class="card-title">My submissions</h6>
            <button type="button" class="btn btn-sm btn-outline-secondary hr-mb-2" :disabled="listLoading" @click="loadList">Refresh</button>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead><tr><th>Year</th><th>Department</th><th>Skill (summary)</th><th>Submitted</th></tr></thead>
                <tbody>
                  <tr v-if="listLoading"><td colspan="4" class="text-muted">Loading…</td></tr>
                  <tr v-else-if="!myRows.length"><td colspan="4" class="text-muted">No submissions yet.</td></tr>
                  <tr v-for="r in myRows" :key="'m-' + r.id">
                    <td>{{ r.audit_year }}</td>
                    <td>{{ r.department }}</td>
                    <td class="text-truncate" style="max-width: 280px" :title="r.skill_description">{{ r.skill_description }}</td>
                    <td>{{ r.created_at }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeSkillsAuditSubmit",
  components: { HrPageLayout },
  data() {
    const y = new Date().getFullYear();
    return {
      auditYear: y,
      yearOptions: [],
      submitting: false,
      listLoading: false,
      msg: "",
      ok: false,
      myRows: [],
      form: {
        skillDescription: "",
        trainingDuration: "",
        evidenceSkills: "",
        trainingNeed: "",
        skillsArea: "",
        howAmIDoing: ""
      }
    };
  },
  mounted() {
    this.loadYears();
  },
  methods: {
    async loadYears() {
      const cur = new Date().getFullYear();
      try {
        const fd = new FormData();
        fd.append("function", "getSkillsAuditYears");
        const res = await execute(fd, authUrl);
        const years = Array.isArray(res?.data?.data?.years) ? res.data.data.years : [];
        const set = new Set([cur, cur - 1, cur - 2, ...years.map((n) => Number(n))]);
        this.yearOptions = Array.from(set)
          .filter((n) => Number.isFinite(n) && n >= 1990 && n <= 2100)
          .sort((a, b) => b - a);
        if (!this.yearOptions.includes(this.auditYear)) {
          this.yearOptions.unshift(this.auditYear);
        }
      } catch (_) {
        this.yearOptions = [cur, cur - 1, cur - 2, cur - 3];
      } finally {
        this.loadList();
      }
    },
    async submit() {
      this.msg = "";
      if (!String(this.form.skillDescription || "").trim()) {
        this.ok = false;
        this.msg = "Description of specific skill is required.";
        return;
      }
      this.submitting = true;
      try {
        const fd = new FormData();
        fd.append("function", "submitMySkillsAudit");
        fd.append("auditYear", String(this.auditYear));
        fd.append("skillDescription", this.form.skillDescription || "");
        fd.append("trainingDuration", this.form.trainingDuration || "");
        fd.append("evidenceSkills", this.form.evidenceSkills || "");
        fd.append("trainingNeed", this.form.trainingNeed || "");
        fd.append("skillsArea", this.form.skillsArea || "");
        fd.append("howAmIDoing", this.form.howAmIDoing || "");
        const res = await execute(fd, authUrl);
        this.ok = !!(res.data && res.data.success);
        this.msg = (res.data && res.data.message) || (this.ok ? "Saved." : "Could not save.");
        if (this.ok) {
          await this.loadList();
        }
      } catch (e) {
        this.ok = false;
        this.msg = e.message || "Could not save.";
      } finally {
        this.submitting = false;
      }
    },
    async loadList() {
      this.listLoading = true;
      try {
        const fd = new FormData();
        fd.append("function", "listMySkillsAuditEntries");
        fd.append("auditYear", String(this.auditYear));
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          this.myRows = res.data.data?.rows || [];
        } else {
          this.myRows = [];
        }
      } catch (_) {
        this.myRows = [];
      } finally {
        this.listLoading = false;
      }
    }
  },
  watch: {
    auditYear() {
      this.loadList();
    }
  }
};
</script>

<style scoped>
.skills-self-wrap {
  width: 100%;
  box-sizing: border-box;
}
.hr-mb-2 {
  margin-bottom: 0.5rem;
}
.hr-mb-3 {
  margin-bottom: 1rem;
}
.skills-form-card {
  border-radius: 8px;
}
.skills-form-scroll {
  max-height: min(70vh, 720px);
  overflow-y: auto;
  padding-right: 6px;
  margin-bottom: 1rem;
}
.form-section {
  padding-bottom: 0;
  margin-bottom: 0;
}
.section-heading {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #334155;
}
.skills-form-actions {
  padding-top: 0.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
